// Enterprise-grade logging system
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4,
}

class Logger {
  private level: LogLevel = import.meta.env.PROD ? LogLevel.ERROR : LogLevel.DEBUG;
  private suppressedPatterns: RegExp[] = [
    /React Router Future Flag Warning/,
    /v7_startTransition/,
    /v7_relativeSplatPath/,
  ];

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private shouldSuppress(message: string): boolean {
    return this.suppressedPatterns.some(pattern => pattern.test(message));
  }

  debug(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.DEBUG) && !this.shouldSuppress(message)) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  info(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.INFO) && !this.shouldSuppress(message)) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.WARN) && !this.shouldSuppress(message)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  error(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  }

  // Override console methods
  setup() {
    const originalWarn = console.warn;
    const originalError = console.error;
    const logger = this;

    console.warn = function(...args: any[]) {
      const message = args[0] || '';
      if (typeof message === 'string' && logger.shouldSuppress(message)) {
        return;
      }
      originalWarn.apply(console, args);
    };

    console.error = function(...args: any[]) {
      const message = args[0] || '';
      if (typeof message === 'string' && logger.shouldSuppress(message)) {
        return;
      }
      originalError.apply(console, args);
    };
  }
}

export const logger = new Logger();
export default logger;
